import { Helmet } from 'react-helmet';

import AseLogo from '../assets/ase-logo.png';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy</title>
      </Helmet>

      <div className='px-4 py-8 max-w-[1024px] mx-auto'>
        <div className='flex flex-col items-center justify-center text-center'>
          <img src={AseLogo} alt="ASE Logo" className='h-[50px] mb-6' />

          <h1 className="text-2xl">Privacy Policy &amp; Terms of Service</h1>
          <h2 className="text-xl mb-1">ASE Group Australia PTY LTD</h2>
          <p className='mb-8 font-bold'>Last Updated: 06/03/25</p>

          <h2 className="text-2xl mb-8">Privacy Policy</h2>

          <div className='text-left flex flex-col gap-4'>
            <p>ASE Group Australia PTY LTD ("ASE Group," "we," "us," or "our") is committed to protecting the privacy of our employees, contractors, and other stakeholders. This Privacy Policy outlines how we collect, use, store, and disclose personal information in compliance with the Australian Privacy Act 1988 (APPs) and the General Data Protection Regulation (GDPR) where applicable.</p>

            <h3 className="text-lg">1. Information We Collect</h3>
            <ul className="list-disc pl-8">
              <li>Full name, date of birth, and contact details (email, phone number, address)</li>
              <li>Employment details (role, job site, work history)</li>
              <li>Licensing and certifications (e.g., scaffolding, rigging, safety tickets)</li>
              <li>Payroll and banking details (for payment processing)</li>
              <li>Emergency contact information</li>
              <li>Work location tracking (if enabled) for workforce management purposes</li>
            </ul>

            <h3 className="text-lg">2. How We Collect Information</h3>
            <ul className="list-disc pl-8">
              <li>Direct input from employees via our workforce management system</li>
              <li>Onboarding forms and compliance documents</li>
              <li>Time tracking and job allocation systems</li>
              <li>Communication with supervisors and managers</li>
            </ul>

            <h3 className="text-lg">3. How We Use Your Information</h3>
            <ul className="list-disc pl-8">
              <li>Workforce scheduling, job allocations, and payroll processing</li>
              <li>Compliance with employment laws and safety regulations</li>
              <li>Communication regarding work assignments and internal notices</li>
              <li>Maintaining employee records for legal and operational requirements</li>
              <li>Improving workforce operations and efficiency</li>
            </ul>

            <h3 className="text-lg">4. Data Storage and Security</h3>
            <ul className="list-disc pl-8">
              <li>All personal data is stored securely on Australian-based servers.</li>
              <li>We implement encryption and access control measures to protect data.</li>
              <li>Only authorized personnel have access to sensitive information.</li>
            </ul>

            <h3 className="text-lg">5. Data Sharing & Third Parties</h3>
            <ul className="list-disc pl-8">
              <li>Payroll providers (e.g., Xero) for salary processing</li>
              <li>Regulatory bodies for compliance (e.g., Safe Work Australia)</li>
              <li>IT service providers to maintain system functionality</li>
              <li>Legal authorities when required by law</li>
            </ul>

            <h3 className="text-lg">6. Employee Rights & Data Access</h3>
            <p>Employees have the right to:</p>
            <ul className="list-disc pl-8">
              <li>Access their personal information</li>
              <li>Request corrections or updates</li>
              <li>Withdraw consent for data processing (where applicable)</li>
              <li>Request deletion of personal data (subject to legal requirements)</li>
            </ul>
            <p>Requests can be made by contacting <a href="mailto:info@ase-group.com.au">info@ase-group.com.au</a>.</p>

            <h3 className="text-lg">7. Data Retention Policy</h3>
            <ul className="list-disc pl-8">
              <li>Employee records are retained for a minimum of 7 years in compliance with Australian law.</li>
              <li>Data is securely deleted once no longer required.</li>
            </ul>

            <h3 className="text-lg">8. Data Breach Notification</h3>
            <p>In the event of a data breach, affected individuals will be notified within 72 hours, and appropriate measures will be taken to mitigate risks.</p>

            <h3 className="text-lg">9. Updates to this Privacy Policy</h3>
            <p>We may update this Privacy Policy from time to time. Any changes will be communicated through our internal platforms.</p>
            <p>For any questions, please contact <a href="mailto:info@ase-group.com.au">info@ase-group.com.au</a>.</p>
          </div>

          <div className='flex flex-col gap-4 text-left my-8'>
            <h2 className="text-2xl mb-4 text-center">Terms of Service</h2>

            <h3 className="text-lg">1. Acceptance of Terms</h3>
            <p>By accessing and using the ASE Group workforce management system ("the Platform"), employees and contractors agree to comply with these Terms of Service.</p>

            <h3 className="text-lg">2. Use of the Platform</h3>
            <ul className="list-disc pl-8">
              <li>Employees must use the Platform solely for work-related purposes.</li>
              <li>Users are responsible for maintaining the confidentiality of their login credentials.</li>
              <li>Unauthorized access or misuse of the system is prohibited.</li>
            </ul>

            <h3 className="text-lg">3. Workplace Communication & Conduct</h3>
            <ul className="list-disc pl-8">
              <li>All communications on the Platform must be professional and respectful.</li>
              <li>Misuse of the Platform, including harassment or inappropriate conduct, may result in disciplinary action.</li>
            </ul>

            <h3 className="text-lg">4. Job Allocation & Workforce Tracking</h3>
            <ul className="list-disc pl-8">
              <li>Employees must confirm job allocations and schedules via the Platform.</li>
              <li>Location tracking, if enabled, is for workforce management purposes only and is not monitored outside work hours.</li>
            </ul>

            <h3 className="text-lg">5. Payroll & Time Tracking</h3>
            <ul className="list-disc pl-8">
              <li>Employees are required to log work hours accurately.</li>
              <li>Any discrepancies must be reported within 7 days for review.</li>
            </ul>

            <h3 className="text-lg">6. Data Ownership & Privacy</h3>
            <ul className="list-disc pl-8">
              <li>ASE Group retains ownership of all workforce-related data.</li>
              <li>Personal data is handled as per the Privacy Policy outlined above.</li>
            </ul>

            <h3 className="text-lg">7. Termination of Access</h3>
            <ul className="list-disc pl-8">
              <li>Access to the Platform may be revoked upon termination of employment.</li>
              <li>Users must return any company-issued devices or credentials.</li>
            </ul>

            <h3 className="text-lg">8. Liability & Disclaimer</h3>
            <ul className="list-disc pl-8">
              <li>ASE Group is not liable for any technical disruptions or data loss arising from third-party service providers.</li>
              <li>Employees are responsible for ensuring compliance with company policies when using the Platform.</li>
            </ul>

            <h3 className="text-lg">9. Amendments to Terms</h3>
            <p>ASE Group may update these Terms of Service at any time. Employees will be notified of significant changes.</p>
            <p>For any queries regarding these Terms, please contact <a href="mailto:info@ase-group.com.au">info@ase-group.com.au</a>.</p>

            <p>By using the Platform, employees acknowledge they have read, understood, and agreed to these Privacy and Terms of Service policies.</p>
          </div>

          <h3 className="text-lg">ASE Group Australia PTY LTD</h3>
          <p>703/27 Cook Street Turrella, NSW 2205</p>
          <p><a href="https://www.ase-group.com.au">www.ase-group.com.au</a></p>
          <p>Contact: <a href="mailto:info@ase-group.com.au">info@ase-group.com.au</a></p>
        </div>
      </div>
    </>
  )
}

export default PrivacyPolicy;
